import {
    DealerCampaignCashOfferFootnoteType,
    DealerCampaignFinanceOfferFootnoteType,
    DealerCampaignLeaserOfferFootnoteType,
    DealerCampaignType,
} from '../interfaces/dealer-campaigns';
import { BUSINESS_OFFER_FORM_BASE_URL, OFFER_FORM_BASE_URL } from './lead-form-url';
import { walkStrings } from './walk-strings';

const privateLinkBaseUrl = OFFER_FORM_BASE_URL;
const businessLinkBaseUrl = BUSINESS_OFFER_FORM_BASE_URL;
const privateKmp = 'KMP-02401-HWS10';
const businessKmp = 'KMP-02401-HWS11';

export function getDealerCampaignLeaserOfferHeadline(footnoteType: DealerCampaignLeaserOfferFootnoteType): string {
    if (
        footnoteType === DealerCampaignLeaserOfferFootnoteType.PK_1 ||
        footnoteType === DealerCampaignLeaserOfferFootnoteType.PK_2 ||
        footnoteType === DealerCampaignLeaserOfferFootnoteType.GK_1 ||
        footnoteType === DealerCampaignLeaserOfferFootnoteType.GK_2
    ) {
        return 'Ihr Hyundai Kilometer Leasing';
    }
    return 'Ein unverbindliches Leasingbeispiel';
}

export type DealerCampaignOfferModelForLink = {
    formIdentifier?: string | null;
    slug?: string | null;
};

export function findDealerCampaignModelByEnvkvModelCode(
    envkvModelCode: string,
    models: Array<{ name: string } & DealerCampaignOfferModelForLink>,
): ({ name: string } & DealerCampaignOfferModelForLink) | undefined {
    return models.find((model) => model.name === envkvModelCode);
}

export function getDealerCampaignOfferSelectModelName(
    envkvModelCode: string,
    model?: DealerCampaignOfferModelForLink | null,
): string {
    const formIdentifier = model?.formIdentifier?.trim();
    if (formIdentifier) {
        return formIdentifier;
    }
    const slug = model?.slug?.trim();
    if (slug) {
        return slug;
    }
    return envkvModelCode;
}

export function getDealerCampaignOfferLinkUrl(
    offer: { email: string; emailSubject: string | null; envkvModelCode: string },
    dealerSapId: string,
    campaignType: DealerCampaignType,
    model?: DealerCampaignOfferModelForLink | null,
): string {
    let linkUrl = `mailto:${offer.email}?subject=${offer.emailSubject ?? ''}`;

    if (offer.envkvModelCode && offer.envkvModelCode.trim().toLowerCase() !== 'anderes modell / älteres modelljahr') {
        const linkBaseUrl = campaignType === DealerCampaignType.BUSINESS ? businessLinkBaseUrl : privateLinkBaseUrl;
        const kmp = campaignType === DealerCampaignType.BUSINESS ? businessKmp : privateKmp;
        const params = new URLSearchParams({
            'dealer-id': dealerSapId,
            campaign: kmp,
            campaignName: 'haendlerwebsite',
            'select-model-name': getDealerCampaignOfferSelectModelName(offer.envkvModelCode, model),
        });
        linkUrl = `${linkBaseUrl}?${params.toString()}`;
    }
    return linkUrl;
}

export function getDealerCampaignFinanceOfferHeadline(footnoteType: DealerCampaignFinanceOfferFootnoteType): string {
    if (footnoteType === DealerCampaignFinanceOfferFootnoteType.PK_1_H) {
        return 'Hyundai Ziel-Finanzierung';
    }
    return 'Hyundai Ziel-Finanzierung';
}

export function getFootnoteForDealerCampaignLeasingOffer(offer: {
    footnoteType: DealerCampaignLeaserOfferFootnoteType;
    transferAndRegistrationPrice: string;
    validUntil: string;
    specificModel: string;
}): string {
    const transferAndRegistrationPrice = formatPrice(offer.transferAndRegistrationPrice);
    if (offer.footnoteType === DealerCampaignLeaserOfferFootnoteType.PK_1) {
        return `Ein unverbindliches Leasingbeispiel für Privatkunden der HYUNDAI Finance, ein Geschäftsbereich der Hyundai Capital Bank Europe GmbH, Europa-Allee 22, 60327 Frankfurt am Main. Bonität vorausgesetzt. Verbraucher haben ein gesetzliches Widerrufsrecht. Nach den Leasingbedingungen besteht die Verpflichtung zum Abschluss einer Vollkaskoversicherung. Kostenpflichtige Zusatzausstattung möglich. Überführungs- und Zulassungskosten in Höhe von ${transferAndRegistrationPrice} EUR für den ${offer.specificModel} werden bei Lieferung des Fahrzeugs separat in Ansatz gebracht. Alle Preise inkl. gesetzlicher MwSt. Angebot gültig bis            ${offer.validUntil}`;
    }
    if (offer.footnoteType === DealerCampaignLeaserOfferFootnoteType.GK_1) {
        return `Ein unverbindliches Leasingbeispiel für Gewerbekunden der HYUNDAI Finance, ein Geschäftsbereich der Hyundai Capital Bank Europe GmbH, Europa-Allee 22, 60327 Frankfurt am Main. Bonität vorausgesetzt. Verbraucher haben ein gesetzliches Widerrufsrecht. Nach den Leasingbedingungen besteht die Verpflichtung zum Abschluss einer Vollkaskoversicherung. Kostenpflichtige Zusatzausstattung möglich. Überführungs- und Zulassungskosten in Höhe von ${transferAndRegistrationPrice} EUR für den ${offer.specificModel} werden bei Lieferung des Fahrzeugs separat in Ansatz gebracht. Alle Preise exkl. gesetzlicher MwSt. Angebot gültig bis ${offer.validUntil}.`;
    }
    if (offer.footnoteType === DealerCampaignLeaserOfferFootnoteType.PK_2) {
        return `Ein unverbindliches Leasingbeispiel für Privatkunden der Hyundai Leasing, ein Service der Allane SE, Dr.-Carl-von-Linde-Str. 2, 82049 Pullach. Bonität vorausgesetzt. Verbraucher haben ein gesetzliches Widerrufsrecht. Nach den Leasingbedingungen besteht die Verpflichtung zum Abschluss einer Vollkaskoversicherung. Kostenpflichtige Zusatzausstattung möglich. Überführungs- und Zulassungskosten in Höhe von ${transferAndRegistrationPrice} EUR für den ${offer.specificModel} werden bei Lieferung des Fahrzeugs separat in Ansatz gebracht. Alle Preise inkl. gesetzlicher MwSt. Angebot gültig bis ${offer.validUntil}`;
    }
    if (offer.footnoteType === DealerCampaignLeaserOfferFootnoteType.GK_2) {
        return `Ein unverbindliches Leasingbeispiel für Gewerbekunden der Hyundai Leasing, ein Service der Allane SE, Dr.-Carl-von-Linde-Str. 2, 82049 Pullach. Bonität vorausgesetzt. Verbraucher haben ein gesetzliches Widerrufsrecht. Nach den Leasingbedingungen besteht die Verpflichtung zum Abschluss einer Vollkaskoversicherung. Kostenpflichtige Zusatzausstattung möglich. Überführungs- und Zulassungskosten in Höhe von ${transferAndRegistrationPrice} EUR für den ${offer.specificModel} werden bei Lieferung des Fahrzeugs separat in Ansatz gebracht. Alle Preise exkl. gesetzlicher MwSt. Angebot gültig bis ${offer.validUntil}.`;
    }
    return '';
}

export function getFootnoteForDealerCampaignCashPriceOffer(offer: {
    footnoteType: DealerCampaignCashOfferFootnoteType;
    transferAndRegistrationPrice: string;
    validUntil: string;
    specificModel: string;
}): string {
    const transferAndRegistrationPrice = formatPrice(offer.transferAndRegistrationPrice);
    if (offer.footnoteType === DealerCampaignCashOfferFootnoteType.PK_1) {
        return `Überführungskosten in Höhe von ${transferAndRegistrationPrice} EUR enthalten. Angebot gültig bis ${offer.validUntil}.`;
    }
    /*
    If (offer.footnoteType === DealerCampaignCashOfferFootnoteType.GK_2) {
        return `Überführungskosten in Höhe von ${transferAndRegistrationPrice} EUR enthalten. Angebot gültig bis ${offer.validUntil}.`;
    }
*/
    return '';
}

export function getFootnoteForDealerCampaignFinanceOffer(offer: {
    footnoteType: DealerCampaignFinanceOfferFootnoteType;
    transferAndRegistrationPrice: string;
    validUntil: string;
    specificModel: string;
}): string {
    const transferAndRegistrationPrice = formatPrice(offer.transferAndRegistrationPrice);
    if (offer.footnoteType === DealerCampaignFinanceOfferFootnoteType.PK_1_H) {
        return `
        Ein unverbindliches Finanzierungsbeispiel der HYUNDAI Finance, ein Geschäftsbereich der Hyundai Capital Bank Europe GmbH, Europa-Allee 22, 60327 Frankfurt am Main. Verbraucher haben ein gesetzliches Widerrufsrecht. Verpflichtung zum Abschluss einer Teilkaskoversicherung. Kostenpflichtige Zusatzausstattung möglich. Überführungs- und Zulassungskosten in Höhe von ${transferAndRegistrationPrice} EUR werden bei Lieferung des Fahrzeugs separat in Ansatz gebracht. Alle Preise inkl. gesetzlicher MwSt. Angebot gültig bis ${offer.validUntil}.`;
    }
    return '';
}

export const DealerCampaignFootnotes = {
    ID_PREFIX: 'dealerCampaign_',

    getFootnoteId(campaignId: number, id: string): string {
        return `${DealerCampaignFootnotes.ID_PREFIX}${campaignId}_${id}`;
    },

    replaceFootnotes<T = unknown>(campaignId: number, data: T): T {
        return walkStrings(data, (value) => {
            return value.replaceAll(
                /#(\d+)#/g,
                (_match, id) => `#f_${DealerCampaignFootnotes.getFootnoteId(campaignId, id)}#`,
            );
        });
    },
};

export const formatPrice = (price: string): string => {
    const normalizedPrice = price.replace(',', '.');
    const numericValue = Number(normalizedPrice);
    return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numericValue);
};

const normalizeStringNumber = (str: string): string => {
    const normalized = str.toString().trim();

    if (normalized.includes(',')) {
        return normalized.replaceAll('.', '').replace(',', '.');
    }

    if (/^[+-]?\d{1,3}(\.\d{3})+$/.test(normalized)) {
        return normalized.replaceAll('.', '');
    }

    return normalized;
};

export const parseStringNumber = (str: string): number => {
    return Number.parseFloat(normalizeStringNumber(str));
};

export const formatStringNumberToLocale = (str: string, digits = 2): string => {
    const float = parseStringNumber(str);
    return Number.isNaN(float)
        ? str
        : float.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export function getDealerCampaignEnvkv(campaignData?: {
    specificModel?: string;
    power?: string;
    envkv?: string;
}): string {
    if (!campaignData) return '';

    const model = campaignData.specificModel?.trim();
    const power = campaignData.power?.trim();
    const envkv = campaignData.envkv?.trim();

    const title = [model, power].filter(Boolean).join(', ');

    if (!title && !envkv) return '';

    if (!title) return envkv ?? '';

    if (!envkv) return title;

    return `${title}: ${envkv}`;
}
